import React, { useState } from "react";
import { toast } from "sonner";
import { Upload } from "lucide-react";
import api from "../../lib/api";

const CHUNK = 2 * 1024 * 1024;

// Upload a backup file in chunks, then replace the whole live database after typed confirmation.
export function RestorePanel() {
  const [file, setFile] = useState(null);
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null);
  const [inputKey, setInputKey] = useState(0);

  const run = async () => {
    if (!file || confirm !== "RESTORE") return;
    setBusy(true);
    try {
      let uploadId = "";
      for (let i = 0, off = 0; off < file.size; i++, off += CHUNK) {
        setStatus({ kind: "info", text: `Uploading ${Math.min(100, Math.round(((off + CHUNK) / file.size) * 100))}%…` });
        const { data } = await api.post(`/admin/restore/upload?index=${i}&offset=${off}&upload_id=${uploadId}`, file.slice(off, off + CHUNK),
          { headers: { "Content-Type": "application/octet-stream" } });
        uploadId = data.upload_id;
      }
      setStatus({ kind: "info", text: "Checking file and restoring… (game pauses briefly)" });
      const { data } = await api.post(`/admin/restore/${uploadId}/apply`, { confirm: "RESTORE" });
      setStatus({ kind: "ok", text: `Restore complete: ${data.collections} collections, ${data.total_docs.toLocaleString()} documents from backup of ${new Date(data.backup_created_at).toLocaleString()}` });
      toast.success("Database restored");
      setFile(null);
      setConfirm("");
      setInputKey((k) => k + 1);
    } catch (e) {
      const d = e.response?.data?.detail;
      setStatus({ kind: "err", text: `Restore failed: ${typeof d === "string" ? d : "unknown error"}` });
      toast.error("Restore failed");
    } finally {
      setBusy(false);
    }
  };

  const color = { ok: "#39FF14", err: "#FF007F", info: "#00F0FF" }[status?.kind] || "#A89BCC";
  return (
    <div className="rounded-xl border border-[#FF007F]/50 bg-[#12082B] p-4 space-y-3" data-testid="admin-restore-panel">
      <div className="font-heading text-sm neon-pink flex items-center gap-2"><Upload size={16} /> Restore database</div>
      <div className="font-mono text-xs text-[#A89BCC]">
        Replaces the WHOLE live database with a backup file. Everything newer than the backup is lost. Current logins stay active. Make a fresh backup first.
      </div>
      <input key={inputKey} data-testid="admin-restore-file" type="file" accept=".gz,application/gzip" disabled={busy}
        onChange={(e) => { setFile(e.target.files?.[0] || null); setStatus(null); }}
        className="block font-mono text-xs text-[#E5DCFF] file:mr-3 file:px-3 file:py-1 file:rounded-lg file:border file:border-[#00F0FF] file:bg-transparent file:text-[#00F0FF]" />
      <div className="flex flex-wrap items-center gap-2">
        <input data-testid="admin-restore-confirm" value={confirm} onChange={(e) => setConfirm(e.target.value)} disabled={busy}
          placeholder="Type RESTORE to confirm"
          className="px-2 py-1.5 rounded-lg bg-[#0A051A] border border-[#FF007F]/40 font-mono text-xs w-56" />
        <button data-testid="admin-restore-btn" disabled={busy || !file || confirm !== "RESTORE"} onClick={run}
          className="px-3 py-1.5 rounded-lg bg-[#FF007F]/20 border border-[#FF007F] text-[#FF007F] hover:bg-[#FF007F]/30 font-heading text-xs uppercase tracking-widest disabled:opacity-40">
          {busy ? "Working…" : "Restore backup"}
        </button>
      </div>
      {status && <div data-testid="admin-restore-status" className="font-mono text-xs" style={{ color }}>{status.text}</div>}
    </div>
  );
}
